import React, { useState, useEffect, useRef } from "react";
import { Typography, Box, Button } from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import { startAnimation } from "../../store/slices/animationSlice";
import { useNavigate } from "react-router-dom";
import {
  buyPoolDayPass,
  buyPoolSeasonPass,
} from "../../services/firebase/database";

function Pool() {
  const [purchaseStatus, setPurchaseStatus] = useState("");
  const [loading, setLoading] = useState(false);
  const [ownedPasses, setOwnedPasses] = useState([]);
  const [balance, setBalance] = useState(null);
  const user = useSelector((state) => state.auth.user);
  const { endPosition } = useSelector((state) => state.animation);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const dayPassButtonRef = useRef(null);
  const seasonPassButtonRef = useRef(null);

  const hasAnyPoolPass = ownedPasses.length > 0;

  const handleEnterPool = () => {
    navigate("/pool/enter");
  };

  useEffect(() => {
    const fetchProfile = async () => {
      if (user) {
        const res = await import("../../services/firebase/database");
        const profile = await res.getUserProfile(user.uid);
        setOwnedPasses(profile.poolPasses || []);
        setBalance(profile.neighborhoodDollars || 0);
      }
    };
    fetchProfile();
  }, [user]);

  const handleBuyDayPass = async () => {
    if (!user) return;
    setLoading(true);
    setPurchaseStatus("");
    try {
      await buyPoolDayPass(user.uid);
      setPurchaseStatus("Day pass purchased!");
      const res = await import("../../services/firebase/database");
      const profile = await res.getUserProfile(user.uid);
      setOwnedPasses(profile.poolPasses || []);
      setBalance(profile.neighborhoodDollars || 0);

      if (dayPassButtonRef.current) {
        const rect = dayPassButtonRef.current.getBoundingClientRect();
        const startPos = {
          x: rect.left + rect.width / 2,
          y: rect.top + rect.height / 2,
        };
        console.log("Pool - Day Pass Start Position:", startPos);
        console.log("Pool - End Position from Redux:", endPosition);
        dispatch(
          startAnimation({
            startPosition: startPos,
            imageSrc: "/dayPass.png",
          })
        );
      }
    } catch (e) {
      setPurchaseStatus(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleBuySeasonPass = async () => {
    if (!user) return;
    setLoading(true);
    setPurchaseStatus("");
    try {
      await buyPoolSeasonPass(user.uid);
      setPurchaseStatus("Season pass purchased!");
      const res = await import("../../services/firebase/database");
      const profile = await res.getUserProfile(user.uid);
      setOwnedPasses(profile.poolPasses || []);
      setBalance(profile.neighborhoodDollars || 0);

      if (seasonPassButtonRef.current) {
        const rect = seasonPassButtonRef.current.getBoundingClientRect();
        const startPos = {
          x: rect.left + rect.width / 2,
          y: rect.top + rect.height / 2,
        };
        console.log("Pool - Season Pass Start Position:", startPos);
        console.log("Pool - End Position from Redux:", endPosition);
        dispatch(
          startAnimation({
            startPosition: startPos,
            imageSrc: "/seasonPass.png",
          })
        );
      }
    } catch (e) {
      setPurchaseStatus(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: "#f2f0e4",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: 3,
      }}
    >
      <Typography
        variant="h2"
        component="h1"
        sx={{
          color: "#000",
          fontWeight: "bold",
          textAlign: "center",
          mb: 4,
        }}
      >
        Community Pool
      </Typography>

      <Box
        sx={{
          position: "relative",
          width: "100%",
          paddingTop: "56.25%", // 16:9 aspect ratio
          maxWidth: 800,
          mb: 4,
          overflow: "hidden",
        }}
      >
        <iframe
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
          }}
          src="https://www.youtube.com/embed/6eQZ3cmGd94?si=we9VTW0zH4miscUl&amp;controls=1&autoplay=1&loop=1&playlist=6eQZ3cmGd94"
          title="YouTube video player"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          referrerPolicy="strict-origin-when-cross-origin"
          allowFullScreen
        ></iframe>
      </Box>

      
      <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
        {hasAnyPoolPass ? (
          <Button
            variant="contained"
            onClick={handleEnterPool}
            sx={{
              backgroundColor: "black",
              "&:hover": { backgroundColor: "black" },
            }}
          >
            Enter the Pool
          </Button>
        ) : (
          <>
            <Button
              ref={dayPassButtonRef}
              variant="contained"
              color="primary"
              onClick={handleBuyDayPass}
              disabled={loading || balance < 3}
              sx={{
                backgroundColor: "black",
                "&:hover": { backgroundColor: "black" },
              }}
            >
              Buy Day Pass (3 ND)
            </Button>
            <Button
              ref={seasonPassButtonRef}
              variant="contained"
              color="secondary"
              onClick={handleBuySeasonPass}
              disabled={loading || balance < 10}
              sx={{
                backgroundColor: "black",
                "&:hover": { backgroundColor: "black" },
              }}
            >
              Buy Season Pass (10 ND)
            </Button>
          </>
        )}
      </Box>
      {purchaseStatus && (
        <Typography
          variant="body1"
          color={
            purchaseStatus.includes("purchased") ? "success.main" : "error"
          }
          sx={{ mb: 2 }}
        >
          {purchaseStatus}
        </Typography>
      )}
      <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
        Your Pool Passes:
      </Typography>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 3 }}>
        {ownedPasses.length === 0 ? (
          <Typography variant="body2">
            You do not own any pool passes yet.
          </Typography>
        ) : (
          ownedPasses.map((pass, idx) => (
            <Typography key={idx} variant="body2">
              {pass.type === "day" ? "Day Pass" : "Season Pass"} — Purchased:{" "}
              {new Date(pass.purchasedAt).toLocaleDateString()}
            </Typography>
          ))
        )}
      </Box>
    </Box>
  );
}

export default Pool;
